// AiTokenBudgetChip — visar en liten estimerad token-budget-indikator i
// AI-assistentens input-bar (#27). Räknar lazy + RAF-debounce:at från
// nuvarande input + canvas-snapshot + honing-prompt så user vet om
// promptens storlek närmar sig modellens context-window INNAN send.

import { useEffect, useState } from 'react';
import { Gauge } from 'lucide-react';
import { checkTokenBudget, type TokenBudgetCheck } from '@/lib/aiTokenBudget';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';

interface AiTokenBudgetChipProps {
  input: string;
  model: string;
  honingPromptChars: number;
  getSnapshot?: () => WorkflowState | MultiSheetWorkflowState | undefined;
}

const TONE: Record<TokenBudgetCheck['tier'], { dot: string; text: string; label: string }> = {
  ok:       { dot: 'bg-emerald-500',  text: 'text-emerald-600 dark:text-emerald-400', label: 'OK' },
  warn:     { dot: 'bg-amber-500',    text: 'text-amber-600 dark:text-amber-400',     label: 'Stor prompt' },
  critical: { dot: 'bg-destructive',  text: 'text-destructive',                       label: 'Överskrids' },
};

export function AiTokenBudgetChip({ input, model, honingPromptChars, getSnapshot }: AiTokenBudgetChipProps) {
  const [check, setCheck] = useState<TokenBudgetCheck | null>(null);

  // Lazy + debounce:at: token-counten är O(snapshot-storleken) så vi vill
  // inte räkna per keystroke. Räknar igen 250 ms efter senaste change.
  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(() => {
      if (cancelled) return;
      try {
        const snapshot = getSnapshot?.();
        const c = checkTokenBudget({
          snapshot,
          // Vi räknar bara den nya prompten — konversationen som redan
          // ligger på "skickad"-sidan finns inte här. Det är en optimistisk
          // räkning men följer pattern:et i sendMessages där prompten
          // är dominanten över historiken för korta sessioner.
          conversation: input.trim() ? [{ content: input }] : [],
          honingPromptChars,
          model,
        });
        setCheck(c);
      } catch {
        setCheck(null);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [input, model, honingPromptChars, getSnapshot]);

  if (!check) return null;
  // Skippa rendering när promptens utilization är så lågt att informationen
  // bara stör (mindre än 5 %). Track:as fortfarande från första input.
  if (check.utilization < 0.05) return null;
  const tone = TONE[check.tier];
  const pct = Math.round(check.utilization * 100);
  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-muted/60 text-[10px] ${tone.text}`}
      data-testid="ai-token-budget-chip"
      title={`Estimerat ${check.estimatedPromptTokens.toLocaleString()} tokens (${pct}% av context-window). Modell: ${model}.`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} aria-hidden="true" />
      <Gauge className="w-3 h-3" aria-hidden="true" />
      <span>~{check.estimatedPromptTokens.toLocaleString()} tok · {pct}%</span>
      <span className="text-muted-foreground">— {tone.label}</span>
    </div>
  );
}
